import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usersService from '../../services/usersService';
import articlesService from '../../services/articlesService';
import engagementService from '../../services/engagementService';
import Header from '../../components/ui/Header';
import ArticleCard from '../homepage/components/ArticleCard';
import Icon from '../../components/AppIcon';
import { toArabicDigits } from '../../utils/numbers';

const PublicUserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [profile, setProfile] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());

  const isOwnProfile = isAuthenticated && user?.id === id;

  useEffect(() => {
    if (isOwnProfile) {
      navigate('/user-profile', { replace: true });
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const hasToken = !!localStorage.getItem('accessToken');
        const [profileRes, followStatusRes] = await Promise.all([
          usersService.getUser(id),
          hasToken ? usersService.getFollowStatus(id).catch(() => ({ data: { isFollowing: false } })) : Promise.resolve({ data: { isFollowing: false } }),
        ]);
        const profileData = profileRes.data;
        setProfile(profileData);
        const followStatus = followStatusRes?.data?.isFollowing ?? profileData?.isFollowedByCurrentUser ?? false;
        setIsFollowing(followStatus);
      } catch {
        setError('تعذّر تحميل الملف الشخصي.');
      } finally {
        setLoading(false);
      }
    };

    const fetchArticles = async () => {
      setArticlesLoading(true);
      try {
        const { data } = await usersService.getUserArticles(id);
        setArticles(Array.isArray(data) ? data : []);
      } catch {
        // Silently fail for articles
      } finally {
        setArticlesLoading(false);
      }
    };

    fetchProfile();
    fetchArticles();
  }, [id, isOwnProfile, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchBookmarkIds = async () => {
      try {
        const { data } = await articlesService.getMyBookmarkIds();
        setBookmarkedIds(new Set(data));
      } catch (err) {
        console.error('Failed to fetch bookmark IDs:', err);
      }
    };
    fetchBookmarkIds();
  }, [isAuthenticated]);

  const handleBookmarkToggle = async (articleId) => {
    if (!isAuthenticated) return;
    try {
      if (bookmarkedIds.has(articleId)) {
        await engagementService.removeBookmark(articleId);
        setBookmarkedIds(prev => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
      } else {
        await engagementService.bookmarkArticle(articleId);
        setBookmarkedIds(prev => new Set(prev).add(articleId));
      }
    } catch (err) {
      console.error('Bookmark toggle failed:', err);
    }
  };

  const handleFollowToggle = async () => {
    if (!isAuthenticated || followLoading) return;
    if (user?.id === id) return; // Cannot follow yourself
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await usersService.unfollowUser(id);
        setIsFollowing(false);
        setProfile(prev => prev ? { ...prev, followerCount: Math.max(0, (prev.followerCount || 0) - 1) } : prev);
      } else {
        await usersService.followUser(id);
        setIsFollowing(true);
        setProfile(prev => prev ? { ...prev, followerCount: (prev.followerCount || 0) + 1 } : prev);
      }
    } catch (err) {
      console.error('Follow toggle failed:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const displayedArticles = articles.map((a) => ({
    id: a.id,
    title: a.title,
    excerpt: a.excerpt,
    image: a.image || a.coverImage,
    imageAlt: a.imageAlt || a.coverImageAlt || a.title,
    author: profile?.fullName || a.author || '',
    authorImage: profile?.avatarUrl || a.authorImage || '',
    authorImageAlt: 'صورة الكاتب',
    circle: a.circle,
    readTime: a.readTime,
    claps: a.claps || 0,
    publishedDate: a.publishedDate || '',
    type: a.type,
    authorId: a.authorId,
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        <div className="flex items-center justify-center pt-40">
          <Icon name="Loader" size={40} color="var(--color-primary)" className="animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        <div className="text-center pt-40">
          <Icon name="UserX" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-lg text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
            {error || 'لم يتم العثور على المستخدم'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 inline-flex items-center gap-2 px-6 py-2 rounded-full bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
            style={{ fontFamily: 'Amiri, serif' }}
          >
            العودة
          </button>
        </div>
      </div>
    );
  }

  const avatarUrl = profile.avatarUrl || profile.avatar_url;
  const displayName = profile.fullName || profile.full_name || 'مستخدم';
  const bio = profile.bio ?? profile.bio ?? '';

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />

      <main className="pt-16 lg:pt-20">
        {/* Profile Header */}
        <section className="py-12 md:py-16 lg:py-20 border-b border-[var(--color-border)]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden shadow-warm-lg">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="صورة المستخدم" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-primary)]/5 flex items-center justify-center">
                    <Icon name="User" size={64} color="var(--color-primary)" />
                  </div>
                )}
              </div>
            </div>

            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-1"
              style={{ fontFamily: 'Aref Ruqaa, serif' }}
            >
              {displayName}
            </h1>

            {profile.title && (
              <p className="text-sm md:text-base text-[var(--color-primary)] mb-3" style={{ fontFamily: 'Amiri, serif' }}>
                {profile.title}
              </p>
            )}

            {/* النبذة التعريفية - always visible when visiting any user profile */}
            <p
              className="text-base md:text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto mb-4"
              style={{ fontFamily: 'Amiri, serif', lineHeight: '1.8' }}
            >
              {(profile.bio ?? '').trim() || 'لم يضف هذا المستخدم نبذة تعريفية بعد'}
            </p>

            {/* Follow Button */}
            {isAuthenticated && !isOwnProfile && (
              <button
                onClick={handleFollowToggle}
                disabled={followLoading}
                className={`inline-flex items-center gap-2 px-6 py-2 rounded-full transition-all duration-300 mb-6 ${
                  isFollowing
                    ? 'bg-transparent border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10'
                    : 'bg-[var(--color-primary)] text-white hover:opacity-90'
                }`}
                style={{ fontFamily: 'Amiri, serif', opacity: followLoading ? 0.6 : 1 }}
              >
                <Icon name={isFollowing ? 'UserCheck' : 'UserPlus'} size={16} />
                {isFollowing ? 'إلغاء المتابعة' : 'متابعة'}
              </button>
            )}

            {/* Stats */}
            <div className="flex items-center justify-center gap-8 text-sm md:text-base">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
                  {toArabicDigits(profile.articleCount ?? 0)}
                </span>
                <span className="text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  منشورات
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
                  {toArabicDigits(profile.totalClaps ?? 0)}
                </span>
                <span className="text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  إعجاب
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
                  {toArabicDigits(profile.followerCount ?? 0)}
                </span>
                <span className="text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  متابعين
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Published Articles Tab Header */}
        <section className="border-b border-[var(--color-border)]">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-center">
              <div
                className="py-4 px-6 text-base md:text-lg font-medium text-[var(--color-primary)] relative"
                style={{ fontFamily: 'Amiri, serif' }}
              >
                منشورات
                <span className="absolute bottom-0 right-0 w-full h-0.5 bg-[var(--color-primary)]" />
              </div>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12 md:py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
            {articlesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="w-full aspect-square bg-[var(--color-border)] rounded-full mx-auto mb-6" style={{ maxWidth: '320px' }} />
                    <div className="space-y-3 text-center px-6">
                      <div className="h-6 bg-[var(--color-border)] rounded w-3/4 mx-auto" />
                      <div className="h-4 bg-[var(--color-border)] rounded w-full mx-auto" />
                    </div>
                  </div>
                ))}
              </div>
            ) : displayedArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {displayedArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    isDiscussion={article.type === 'Discussion'}
                    isBookmarked={bookmarkedIds.has(article.id)}
                    onBookmarkToggle={handleBookmarkToggle}
                    isAuthenticated={isAuthenticated}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Icon name="FileText" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h3
                  className="text-xl md:text-2xl font-bold text-[var(--color-foreground)] mb-2"
                  style={{ fontFamily: 'Aref Ruqaa, serif' }}
                >
                  لا توجد منشورات بعد
                </h3>
                <p className="text-base text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  لم ينشر هذا المستخدم أي مقالات حتى الآن
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default PublicUserProfile;
